// A control caught mid-fieldwork, so the three states a test can be in sit on one screen.
//
// Interim testing has concluded, the rollforward is being worked through now, and a year-end test
// the plan calls for has not been started. The card reads the same in all three: a description over
// a fold, holding the plan where no test exists yet and the test itself where one does.
//
// Titles and taglines track the design document so a walkthrough can follow it section by section.
import {BasisItem, DateRange, DemoControl, Scenario} from "../model";
import {generateParameters, procedureById} from "../procedures";

const FY26 = {from: "2026-01-01", to: "2026-12-31"};
const INTERIM = {from: "2026-01-01", to: "2026-09-30"};
const ROLLFORWARD = {from: "2026-10-01", to: "2026-12-31"};

const reconciliationReview = procedureById("lib-reconciliation-review");

const payrollReconciliation: DemoControl = {
  ref: "PAY-06",
  title: "Payroll reconciliation review",
  assessment: "Payroll",
  frequency: "monthly",
  scopes: [{scope: "Global", design: "effective", implementation: "effective"}],
};

/** A test the plan has specified and nobody has started. */
const plannedTest = (id: string, span: DateRange, goal: string, sampleSize: number): BasisItem => ({
  id,
  span,
  status: "planned",
  evidence: {
    kind: "test",
    goal,
    procedure: reconciliationReview && {
      id: reconciliationReview.id,
      name: reconciliationReview.name,
      version: reconciliationReview.version,
    },
    parameters: {
      ...generateParameters(goal, {
        procedure: reconciliationReview,
        population: "Monthly payroll reconciliations",
        frequency: "monthly",
        singleItem: sampleSize === 1,
      }),
      sampleSize,
    },
  },
  conditions: [],
});

const interim: BasisItem = {
  id: "b1",
  status: "obtained",
  span: INTERIM,
  evidence: {
    kind: "test",
    label: "PAY-06 interim",
    itemsTested: 6,
    exceptions: 0,
    outcome: "operated_effectively",
    conclusion:
      "All six reconciliations tested were prepared within five working days of month end and " +
      "reviewed by somebody other than the preparer, with no items left unexplained.",
  },
  conditions: [],
};

const yearEnd = plannedTest(
  "b3",
  {from: "2026-12-01", to: "2026-12-31"},
  "Establish that the December reconciliation, which carries the year-end balance, was prepared and reviewed before the accounts were closed.",
  1,
);

export const fieldworkUnderway: Scenario = {
  id: "s11",
  section: "Scenario 11",
  title: "A control part-way through fieldwork",
  tagline: "planned, running and concluded, all at once",
  situation:
    "PAY-06 at the point the audit is actually at. Interim testing has concluded, the rollforward " +
    "sample is being worked through now, and the dedicated year-end test has not been started. " +
    "Nothing about the item changes between the three — only what its fold holds.",
  focus: {control: "PAY-06", scope: "Global"},
  controls: [payrollReconciliation],
  edges: [],
  oe: [
    {
      control: "PAY-06",
      scope: "Global",
      period: FY26,
      // The rollforward is being worked as the scenario opens; the year-end test is not.
      underway: {b2: {tested: 2, exceptions: 1}},
      basis: [
        interim,
        plannedTest(
          "b2",
          ROLLFORWARD,
          "Establish that the reconciliation review operated over October to December, as it did at interim.",
          3,
        ),
        yearEnd,
      ],
      conclusion: {
        outcome: "insufficient_evidence",
        supportsReliance: false,
        justification:
          "Interim testing covers January to September with no exceptions. The rollforward is still " +
          "being worked and one exception has been seen so far, so the last quarter cannot be " +
          "concluded on yet — the argument is sound, the evidence for it is not all in.",
      },
    },
  ],
};
